import { Card, Checkbox, Col, Divider, Form, Input, Modal, Row, Spin, Tag } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { dateTimeDisplayFormat, formatDisplayDate, ModalWidth, ROW_GUTTER } from '@/config';
import SeatingModelInput from '@/components/FormItems/SeatingModelInput';
import RouteInput from '@/components/FormItems/RouteInput';
import TripGroupInput from '@/components/FormItems/TripGroupInput';
import './styles.less';
import { DayOfWeekType } from '@/constants';
import { range } from 'lodash';
import moment from 'moment';
import { TripStopModel, RouteBasicInfoModel, TripModel, RouteStopDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
type Props = {
  onClose: () => void;
  tripId: string;
};

interface TripStopModelDetail extends TripStopModel {
  stationName: string;
}
export default function BaseTripDetailModal({ onClose, tripId }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [routeDetail, setRouteDetail] = React.useState<RouteBasicInfoModel>();
  const [stops, setStops] = React.useState<TripStopModelDetail[]>([]);
  const [triDetail, setTripDetail] = React.useState<TripModel>();

  const getTripDetail = () => {
    setLoading(true);
    apiSdk.TripService.getDetails({ id: tripId })
      .then(res => {
        setTripDetail(res);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    if (triDetail) {
      getRouteData();
    }
  }, [triDetail]);

  const getRouteData = () => {
    if (triDetail?.routeId) {
      const apis = [apiSdk.RouteService.getBasicInfo({ id: triDetail.routeId }), apiSdk.RouteService.getRouteStops({ routeId: triDetail.routeId })];
      Promise.all(apis)
        .then(res => {
          const routeStops = res[1] as RouteStopDto[];
          setRouteDetail(res[0] as any);
          setStops(
            triDetail.tripStops?.map(stop => {
              return {
                ...stop,
                stationName: routeStops.find(x => x.stationId === stop.stationId)?.stationName || '',
              };
            }) || [],
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };
  React.useEffect(() => {
    if (tripId) {
      getTripDetail();
    }
  }, [tripId]);

  const recurrenceDates = React.useMemo(() => {
    if (triDetail?.isRecurred && triDetail.plannedDepartureDateTime && triDetail?.recurrenceNumDays?.length) {
      const dates: string[] = [];
      range(1, 31).forEach(i => {
        let date = moment().add(i, 'day');
        let numOfWeek = date.day();
        if (triDetail?.recurrenceNumDays?.includes(numOfWeek)) {
          dates.push(formatDisplayDate(date));
        }
      });
      return dates;
    }
    return [];
  }, [triDetail]);

  return (
    <Modal
      style={{ top: 10 }}
      open={true}
      maskClosable={false}
      width={ModalWidth.extraLarge}
      className="nta-custom-form create-edit-trip-modal"
      title={t('tripsPage_TripDetail')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={() => {
        onClose();
      }}
    >
      <Spin spinning={loading}>
        <Form layout="vertical">
          <Row gutter={ROW_GUTTER}>
            <Col md={8}>
              <Card className="mtopo-card" size="small" title={t('tripsPage_TripDetail')}>
                <Form.Item label={t('tripsPage_TripName')}>
                  <Input disabled className="input-value-color" value={triDetail?.tripName} />
                </Form.Item>
                <Form.Item label={t('tripsPage_SeatingModel')}>
                  <SeatingModelInput disabled className="input-value-color" value={triDetail?.seatingModelId} />
                </Form.Item>
                <Form.Item label={t('tripsPage_Route')}>
                  <RouteInput disabled className="input-value-color" value={triDetail?.routeId} />
                </Form.Item>
                <Form.Item label={t('tripsPage_Group')}>
                  <TripGroupInput disabled className="input-value-color" value={triDetail?.tripGroupId} />
                </Form.Item>
                <Form.Item>
                  <Checkbox checked={triDetail?.isActive}>{t('base.IsActive')}</Checkbox>
                </Form.Item>
                <Form.Item>
                  <Checkbox checked={triDetail?.isRecurred}>{t('tripsPage_IsRecurred')}</Checkbox>
                </Form.Item>
                {triDetail?.isRecurred ? (
                  <>
                    <div dangerouslySetInnerHTML={{ __html: t('tripsPage_RecurTripNote') }} />
                    <b>{t('tripsPage_TripRecurrenceDays')}</b>

                    <Checkbox.Group options={DayOfWeekType.__getSelectOptions()} value={triDetail.recurrenceNumDays} />

                    <Divider />
                    {recurrenceDates.map(date => {
                      return (
                        <Tag key={date} color="blue" style={{ marginBottom: 2, marginRight: 4 }}>
                          {date}
                        </Tag>
                      );
                    })}
                  </>
                ) : null}
              </Card>
            </Col>
            <Col md={16}>
              <Card className="mtopo-card" size="small" title={t('tripsPage_RouteTripDetail')} bodyStyle={{ backgroundColor: 'white' }}>
                {triDetail ? (
                  <>
                    <Row gutter={ROW_GUTTER} className="components-row-header">
                      <Col md={7}>{t('tripsPage_Source')}</Col>
                      <Col md={6}>{t('tripsPage_ArrivalDateTime')}</Col>
                      <Col md={6}>{t('tripsPage_DepartureDateTime')}</Col>
                      <Col md={5}>{t('tripsPage_ConnectionPoint')}</Col>
                    </Row>
                    <Row gutter={ROW_GUTTER} className="components-row-item" style={{ lineHeight: '47px' }}>
                      <Col md={13}>
                        <b>{routeDetail?.sourceStationName}</b>
                      </Col>

                      <Col md={6}>
                        <Input
                          disabled
                          className="input-value-color w-100"
                          value={dateTimeDisplayFormat.originDateTime(triDetail?.plannedDepartureDateTime)}
                        />
                      </Col>
                      <Col md={5} style={{ textAlign: 'center' }}></Col>
                    </Row>

                    {stops.map((stop, index) => {
                      return (
                        <Row gutter={ROW_GUTTER} className="components-row-item" style={{ lineHeight: '47px' }} key={index}>
                          <Col md={7}>
                            <b>{stop.stationName}</b>
                          </Col>

                          <Col md={6}>
                            <Input
                              disabled
                              className="input-value-color w-100"
                              value={dateTimeDisplayFormat.originDateTime(stop?.plannedArrivalDateTime)}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              disabled
                              className="input-value-color w-100"
                              value={dateTimeDisplayFormat.originDateTime(stop?.plannedDepartureDateTime)}
                            />
                          </Col>
                          <Col md={5} style={{ textAlign: 'center' }}>
                            <Checkbox checked={stop.isConnectionPoint} />
                          </Col>
                        </Row>
                      );
                    })}
                    <Row gutter={ROW_GUTTER} className="components-row-item" style={{ lineHeight: '47px' }}>
                      <Col md={7}>
                        <b>{routeDetail?.destinationStationName}</b>
                      </Col>
                      <Col md={6}>
                        <Input
                          disabled
                          className="input-value-color w-100"
                          value={dateTimeDisplayFormat.originDateTime(triDetail?.plannedArrivalDateTime)}
                        />
                      </Col>
                      <Col md={6}></Col>
                      <Col md={5} style={{ textAlign: 'center' }}></Col>
                    </Row>
                  </>
                ) : null}
              </Card>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
